import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AdminLayout from '../components/AdminLayout';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Users, CreditCard, Activity, TrendingUp, Zap, ArrowUpRight, BarChart3 } from 'lucide-react';
import { adminAPI } from '../../utils/api';
// Chart library imported but not currently used
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function AdminHome() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [isAuthenticated, user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data.data || {});
    } catch (error) {
      console.error('[AdminHome] Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
          <div className="text-[#A0AEC0] text-lg">{t('Loading dashboard...')}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#0B0E14] relative overflow-hidden">
        {/* Background Accent Gradient */}
        <div className="fixed inset-0 bg-gradient-to-br from-[#128C7E]/5 via-transparent to-transparent pointer-events-none" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Page Header */}
          <div className="mb-8 sm:mb-12 lg:mb-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 leading-tight">
              {t('Admin Dashboard')}
            </h1>
            <p className="text-base sm:text-lg text-[#A0AEC0] max-w-3xl leading-relaxed">
              {t('Welcome to the admin panel. Manage your system here.')}
            </p>
          </div>

          {/* Stats Grid - 4 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
            {/* Total Users Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <Users className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Total Users')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.totalUsers ? new Intl.NumberFormat(i18n.language).format(stats.totalUsers) : '0'}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[#128C7E] text-xs sm:text-sm font-semibold">
                      <ArrowUpRight className="w-3 sm:w-4 h-3 sm:h-4" />
                      +8%
                    </span>
                    <span className="text-[#718096] text-xs">{t('this month')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Plans Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <CreditCard className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Total Plans')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.totalPlans ? new Intl.NumberFormat(i18n.language).format(stats.totalPlans) : '0'}
                  </p>
                  <div className="inline-block">
                    <span className="text-[#128C7E] text-xs sm:text-sm font-semibold">{t('Active')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total API Keys Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <Zap className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Total API Keys')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.totalApiKeys ? new Intl.NumberFormat(i18n.language).format(stats.totalApiKeys) : '0'}
                  </p>
                  <div className="text-[#A0AEC0] text-xs">{t('Active keys')}</div>
                </div>
              </div>
            </div>

            {/* Total OTP Requests Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <TrendingUp className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Total OTP Requests')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.totalOtpRequests ? new Intl.NumberFormat(i18n.language).format(stats.totalOtpRequests) : '0'}
                  </p>
                  <div className="text-[#A0AEC0] text-xs">{t('All time')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16">
            {/* Successful Deliveries Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#10B981]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#10B981]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <Activity className="w-5 sm:w-7 h-5 sm:h-7 text-[#10B981]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Successful Deliveries')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.successfulDeliveries ? new Intl.NumberFormat(i18n.language).format(stats.successfulDeliveries) : '0'}
                  </p>
                  <div className="inline-block">
                    <span className="text-[#10B981] text-xs sm:text-sm font-semibold">{t('Excellent')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Failed Deliveries Card */}
            <div className="group">
              <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#EF4444]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#EF4444]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                  <Activity className="w-5 sm:w-7 h-5 sm:h-7 text-[#EF4444]" />
                </div>

                <div>
                  <p className="text-[#A0AEC0] text-xs sm:text-sm font-medium mb-2 sm:mb-3">{t('Failed Deliveries')}</p>
                  <p className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
                    {stats?.failedDeliveries ? new Intl.NumberFormat(i18n.language).format(stats.failedDeliveries) : '0'}
                  </p>
                  <div className="text-[#A0AEC0] text-xs">{t('Needs attention')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Management Cards Section */}
          <div className="mb-8 sm:mb-12 lg:mb-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Users Management */}
              <div 
                className="group cursor-pointer"
                onClick={() => navigate('/admin/users')}
              >
                <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                  <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                    <Users className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                  </div>

                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2">{t('Manage Users')}</h3>
                    <p className="text-[#A0AEC0] text-sm mb-6">{t('Manage all system users and their roles')}</p>
                    <button className="text-[#128C7E] hover:text-[#128C7E]/80 text-sm font-semibold transition-colors flex items-center gap-2">
                      {t('Go to Users')} →
                    </button>
                  </div>
                </div>
              </div>

              {/* Plans Management */}
              <div 
                className="group cursor-pointer"
                onClick={() => navigate('/admin/plans')}
              >
                <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                  <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                    <CreditCard className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                  </div>

                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2">{t('Manage Plans')}</h3>
                    <p className="text-[#A0AEC0] text-sm mb-6">{t('Manage billing plans and features')}</p>
                    <button className="text-[#128C7E] hover:text-[#128C7E]/80 text-sm font-semibold transition-colors flex items-center gap-2">
                      {t('Go to Plans')} →
                    </button>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div 
                className="group cursor-pointer"
                onClick={() => navigate('/admin/stats')}
              >
                <div className="relative bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 hover:border-[#128C7E]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/10">
                  <div className={`absolute top-4 sm:top-6 w-12 sm:w-14 h-12 sm:h-14 bg-[#128C7E]/10 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:bg-[#128C7E]/20 transition-all ${isRTL ? 'left-4 sm:left-6' : 'right-4 sm:right-6'}`}>
                    <BarChart3 className="w-5 sm:w-7 h-5 sm:h-7 text-[#128C7E]" />
                  </div>

                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2">{t('View Statistics')}</h3>
                    <p className="text-[#A0AEC0] text-sm mb-6">{t('View system statistics')}</p>
                    <button className="text-[#128C7E] hover:text-[#128C7E]/80 text-sm font-semibold transition-colors flex items-center gap-2">
                      {t('Go to Stats')} →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </AdminLayout>
  );
}
